import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, X, Save, Star } from "lucide-react";
import { toast } from "sonner";
import {
  fetchReviews, adminCreateReview, adminUpdateReview, adminDeleteReview,
} from "@/lib/api";
import { invalidateReviews } from "@/hooks/useSiteContent";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const EMPTY = { author: "", rating: 5, text: "", sort_order: 999 };

export const AdminReviews = ({ token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchReviews();
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    const next = (items.reduce((m, x) => Math.max(m, x.sort_order || 0), 0) || 0) + 1;
    setEditing({ ...EMPTY, sort_order: next });
  };

  const save = async () => {
    if (!editing.author.trim()) return toast.error("Author is required");
    if (!editing.text.trim()) return toast.error("Review text is required");
    setSaving(true);
    const payload = {
      author: editing.author.trim(),
      rating: Math.max(1, Math.min(5, Number(editing.rating) || 5)),
      text: editing.text.trim(),
      sort_order: Number(editing.sort_order) || 999,
    };
    try {
      if (editing.id) {
        await adminUpdateReview(editing.id, payload, token);
        toast.success("Updated");
      } else {
        await adminCreateReview(payload, token);
        toast.success("Added");
      }
      invalidateReviews();
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Remove review from "${item.author}"?`)) return;
    try {
      await adminDeleteReview(item.id, token);
      invalidateReviews();
      toast.success("Removed");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const sorted = [...items].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  return (
    <div data-testid="admin-reviews">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-3xl text-white">Customer Reviews</h2>
          <p className="text-white/50 text-sm uppercase tracking-widest mt-1">{items.length} reviews shown on the site</p>
        </div>
        <button data-testid="review-add-btn" onClick={openNew} className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF3333] text-white px-4 h-10 uppercase tracking-widest text-xs font-bold border-2 border-transparent hover:border-white">
          <Plus className="w-4 h-4" /> Add Review
        </button>
      </div>

      {loading ? (
        <div className="text-center py-20 text-white/50 uppercase tracking-widest">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {sorted.map((r) => (
            <article key={r.id} data-testid={`review-card-${r.id}`} className="border-2 border-[#222] bg-[#1A1A1A] p-4 hover:border-[#E60000] transition-colors">
              <div className="flex justify-between items-start gap-3 mb-2">
                <div>
                  <div className="font-heading text-xl text-white">{r.author}</div>
                  <div className="flex gap-0.5 mt-1">
                    {Array.from({ length: r.rating || 5 }).map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-[#E60000] text-[#E60000]" strokeWidth={0} />)}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <button onClick={() => setEditing({ ...r })} data-testid={`review-edit-${r.id}`} className="w-8 h-8 flex items-center justify-center bg-[#0A0A0A] border border-[#333] hover:border-[#E60000] hover:text-[#E60000] text-white/70"><Edit className="w-4 h-4" /></button>
                  <button onClick={() => remove(r)} data-testid={`review-delete-${r.id}`} className="w-8 h-8 flex items-center justify-center bg-[#0A0A0A] border border-[#333] hover:bg-[#E60000] hover:border-[#E60000] text-white/70 hover:text-white"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <p className="text-white/70 text-sm italic line-clamp-4">"{r.text}"</p>
            </article>
          ))}
        </div>
      )}

      {editing && (
        <div data-testid="review-editor" className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-[#141414] border-2 border-[#E60000] max-w-xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b-2 border-[#333] bg-black flex items-center justify-between">
              <h3 className="font-heading text-2xl text-white">{editing.id ? "Edit Review" : "New Review"}</h3>
              <button onClick={() => setEditing(null)} className="w-9 h-9 flex items-center justify-center text-white/70 hover:text-white hover:bg-[#E60000]"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/70 font-bold mb-2 block">Author / Customer Name</label>
                <Input value={editing.author} onChange={(e) => setEditing((c) => ({ ...c, author: e.target.value }))} className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-11 text-white" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-white/70 font-bold mb-2 block">Rating (1–5 stars)</label>
                  <Input type="number" min="1" max="5" value={editing.rating} onChange={(e) => setEditing((c) => ({ ...c, rating: e.target.value }))} className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-11 text-white" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-white/70 font-bold mb-2 block">Sort order</label>
                  <Input type="number" value={editing.sort_order} onChange={(e) => setEditing((c) => ({ ...c, sort_order: e.target.value }))} className="bg-[#0A0A0A] border-2 border-[#333] rounded-none h-11 text-white" />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/70 font-bold mb-2 block">Review text</label>
                <Textarea rows={6} value={editing.text} onChange={(e) => setEditing((c) => ({ ...c, text: e.target.value }))} className="bg-[#0A0A0A] border-2 border-[#333] rounded-none text-white text-sm" />
              </div>
            </div>
            <div className="px-6 py-4 border-t-2 border-[#333] bg-black flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="bg-transparent border-2 border-[#333] hover:border-[#E60000] text-white/70 hover:text-white px-4 h-10 uppercase tracking-widest text-xs font-bold">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="review-save" className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF3333] text-white px-5 h-10 uppercase tracking-widest text-xs font-bold border-2 border-transparent hover:border-white disabled:opacity-50">
                <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
